import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, Menu, X, Car, Sun, Moon } from 'lucide-react';
import { useLocation, Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';

const NAV_ITEMS = [
  { label: 'Home', path: '/' },
  { label: 'About', path: '/about' },
  { label: 'Contact', path: '/contact' },
];

const PLAY_STORE_RIDER =
  'https://play.google.com/store/apps/details?id=com.elt.totowala';

const PLAY_STORE_DRIVER =
  'https://play.google.com/store/apps/details?id=com.elt.totowalafordrivers';


const HeaderSection = () => {
  const location = useLocation();
  const { isDark, toggleTheme } = useTheme();

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const [forceTransparent, setForceTransparent] = useState(false);

  const cur = location.pathname;


  useEffect(() => {
    setIsMenuOpen(false);
  }, [cur]);


  useEffect(() => {
    const handler = (e) => {
      setForceTransparent(e.detail.transparent);
    };

    window.addEventListener('header-transparency', handler);

    return () => {
      window.removeEventListener('header-transparency', handler);
    };
  }, []);


  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);


  const navText = isDark
  ? 'text-white hover:text-gray-200'
  : 'text-gray-900 hover:text-black';

const navActive = isDark
  ? 'text-white'
  : 'text-black';



  return (
    <>
      {!forceTransparent && (
        <div className="h-14 shrink-0" />
      )}


      <header
  className={`fixed top-0 left-0 right-0 z-[200] h-14 border-b ${
    isDark
      ? 'bg-gray-900 border-gray-700'
      : 'bg-white border-gray-200'
  }`}
>
      

        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 h-full flex items-center justify-between">


          {/* LOGO */}
          <Link
            to="/"
            onClick={() => setIsMenuOpen(false)}
            className="flex items-center"
          >
            <img
              src="/images/totowala-logo.svg"
              alt="TotoWala"
              className="h-7 w-auto"
            />
          </Link>



          {/* DESKTOP NAV */}
          <nav className="hidden lg:flex items-center gap-6">

            {NAV_ITEMS.map(({label,path}) => {

              const active = cur === path;

              return (
                <Link
                  key={path}
                  to={path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`text-sm font-medium transition-colors ${
                    active
                    ? `${navActive} font-semibold`
                    : navText
                  }`}
                >
                  {label}
                </Link>
              )

            })}

          </nav>
                    {/* RIGHT SIDE */}
          <div className="flex items-center gap-4">


            {/* DESKTOP BUTTONS */}
            <div className="hidden lg:flex items-center gap-4">

              <a
                href={PLAY_STORE_DRIVER}
                target="_blank"
                rel="noopener noreferrer"
                className={`text-sm font-medium ${navText}`}
              >
                For Drivers
              </a>


              <a
                href={PLAY_STORE_RIDER}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700"
              >
                Download App
              </a>

            </div>


            {/* THEME BUTTON */}
            <button
              onClick={toggleTheme}
              className={`w-9 h-9 flex items-center justify-center rounded-full ${
                forceTransparent
                ? 'text-white' 
                : 'text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
              aria-label="Toggle theme"
            >

{isDark ? (
  <Sun size={20} className="text-white" />
) : (
  <Moon size={20} className="text-gray-900" />
)}
            
            </button>

            {/* MOBILE MENU BUTTON */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`lg:hidden w-9 h-9 flex items-center justify-center ${
                forceTransparent
                ? 'text-white'
                : 'text-gray-900 dark:text-white'
              }`}
            >

              {isMenuOpen
                ? <X size={20}/>
                : <Menu size={20}/>
              }

            </button>


          </div>


        </div>

      </header>


      {/* MOBILE MENU */}

      <AnimatePresence>

      {isMenuOpen && (

        <motion.div

          initial={{opacity:0,height:0}}
          animate={{opacity:1,height:'auto'}}
          exit={{opacity:0,height:0}}

          className="lg:hidden fixed top-14 left-0 right-0 z-[199] bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700"

        >


          <div className="px-4 py-4 flex flex-col gap-2">


            {NAV_ITEMS.map(({label,path}) => (

              <Link

                key={path}
                to={path}
                onClick={() => setIsMenuOpen(false)}

                className={`text-left py-3 ${
                  cur === path
                  ? 'text-black dark:text-white font-semibold'
                  : 'text-gray-700 dark:text-gray-300'
                }`}

              >

                {label}

              </Link>

            ))}

            <div className="border-t border-gray-200 dark:border-gray-700 my-2"/>



            <a

              href={PLAY_STORE_DRIVER}
              target="_blank"
              rel="noopener noreferrer"

              className="flex items-center gap-2 py-3 text-gray-700 dark:text-gray-300"

            >

              <Car size={16}/>
              For Drivers

            </a>

            <a

              href={PLAY_STORE_RIDER}
              target="_blank"
              rel="noopener noreferrer"

              className="flex items-center justify-center gap-2 py-3 rounded-full bg-blue-600 text-white font-semibold"

            >

              <Download size={16}/>
              Download App

            </a>


          </div>


        </motion.div>

      )}

      </AnimatePresence>


    </>
  );
};


export default HeaderSection;